/**
 * Fumbo ERP design tokens (UI/UX Specification §7; NFR-UI-01; ADR-012).
 *
 * Two brand colours come from the supplied wordmark: ink navy `#101333` and ember
 * orange `#f16723`. Everything else is a cool, low-saturation neutral scale so data
 * reads first and colour only ever *adds* meaning (status is never colour alone, §8).
 *
 * Light mode uses ink as the primary interactive colour; dark mode inverts it, the way
 * monochrome SaaS interfaces do, so buttons stay high-contrast on navy-black surfaces.
 * Ember is the accent: current-item markers, key numbers, the occasional call to action.
 */
import type { ThemeDefinition } from 'vuetify'

const brand = {
  ink: '#101333',
  ember: '#f16723',
} as const

export const light: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#f5f6fa',
    surface: '#ffffff',
    'surface-bright': '#ffffff',
    'surface-light': '#f0f2f8',
    'surface-variant': '#e9ecf4',
    'on-surface-variant': '#4b5170',
    primary: '#1c2150',
    'on-primary': '#ffffff',
    secondary: '#5b6078',
    'on-secondary': '#ffffff',
    accent: brand.ember,
    'on-accent': '#ffffff',
    // Semantic inks deep enough to pass 4.5:1 as tonal chip / button text on white and
    // on surface-light (UX review §2.1); white on them stays above 6:1.
    success: '#15714b',
    'on-success': '#ffffff',
    warning: '#8e5500',
    'on-warning': '#ffffff',
    error: '#af3447',
    'on-error': '#ffffff',
    info: '#225cca',
    'on-info': '#ffffff',
    'on-background': '#14172b',
    'on-surface': '#14172b',
  },
  variables: {
    'border-color': '#14172b',
    'border-opacity': 0.09,
    'high-emphasis-opacity': 0.92,
    'medium-emphasis-opacity': 0.62,
    'disabled-opacity': 0.4,
    'hover-opacity': 0.04,
    'focus-opacity': 0.08,
    'activated-opacity': 0.08,
    'pressed-opacity': 0.1,
    'theme-code': '#0f1225',
    'theme-on-code': '#e8eaf3',
  },
}

export const dark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#0b0d1a',
    surface: '#12152a',
    'surface-bright': '#1b1f38',
    'surface-light': '#181c33',
    'surface-variant': '#1e2340',
    'on-surface-variant': '#a3a9c8',
    primary: '#eef0fa',
    'on-primary': brand.ink,
    secondary: '#9ba1c2',
    'on-secondary': '#0b0d1a',
    accent: '#ff7d3d',
    'on-accent': '#1a0d05',
    success: '#3ec48a',
    'on-success': '#04211a',
    warning: '#f0b04a',
    'on-warning': '#2a1b00',
    error: '#ff6b81',
    'on-error': '#2b060d',
    info: '#6ea1ff',
    'on-info': '#051235',
    'on-background': '#eceef8',
    'on-surface': '#eceef8',
  },
  variables: {
    'border-color': '#c9cde8',
    'border-opacity': 0.12,
    'high-emphasis-opacity': 0.94,
    'medium-emphasis-opacity': 0.66,
    'disabled-opacity': 0.42,
    'hover-opacity': 0.06,
    'focus-opacity': 0.1,
    'activated-opacity': 0.1,
    'pressed-opacity': 0.14,
    'theme-code': '#05060f',
    'theme-on-code': '#dfe2f0',
  },
}
